import * as React from "react";
import { cn } from "../../lib/cn";

// ── List container ───────────────────────────────────────────────────────

export interface SurfaceListProps extends React.HTMLAttributes<HTMLUListElement> {
  /**
   * Optional section label rendered above the list (small uppercase, e.g.
   * "Recenti", "Suggeriti da Ando", "Da fare oggi").
   */
  label?: React.ReactNode;
  /** AI/proactive variant — adds the sparkle icon styling. */
  ai?: boolean;
  /**
   * Inbox/mail-style layout: no gap between items, each row consumes
   * full surface width with a 1px bottom border (mirrors the v18 mockup
   * `.inbox-list` + `.inbox-item` pair).
   *
   * Use when the rows are visually distinct already (avatar + meta lines)
   * and the card chrome would only add noise. Default (false) keeps the
   * card-style 6px-gap layout used by docs/tasks/drawings.
   */
  flat?: boolean;
}

/**
 * SurfaceList — DRY ul wrapper used across every surface for vertical
 * collections (inbox items, docs, tasks, drawings, …). Adds a small label
 * + consistent spacing so individual feature components can stay focused
 * on row content.
 */
export const SurfaceList = React.forwardRef<HTMLUListElement, SurfaceListProps>(
  ({ label, ai, flat, className, children, ...rest }, ref) => (
    <div className="flex flex-col gap-2">
      {label ? (
        <div
          className={cn(
            "flex items-center gap-1.5 font-semibold text-[10px] uppercase tracking-[0.1em]",
            ai ? "text-accent" : "text-ink-3",
          )}
        >
          {ai ? <span className="text-[12px]" aria-hidden>✦</span> : null}
          <span>{label}</span>
        </div>
      ) : null}
      <ul
        ref={ref}
        className={cn(flat ? "flex flex-col" : "flex flex-col gap-1.5", className)}
        {...rest}
      >
        {children}
      </ul>
    </div>
  ),
);
SurfaceList.displayName = "SurfaceList";

// ── List item — generic avatar + title + meta + actions ──────────────────

export interface SurfaceListItemProps
  extends Omit<React.HTMLAttributes<HTMLLIElement>, "title"> {
  /** Leading slot — usually a colored avatar / icon chip / checkbox. */
  leading?: React.ReactNode;
  /** Primary row title. */
  title: React.ReactNode;
  /** Optional subtitle / preview line under the title. */
  subtitle?: React.ReactNode;
  /** Meta line above the title (e.g. channel · author · time). */
  meta?: React.ReactNode;
  /** Right-aligned trailing slot — timestamp / badge / chevron. */
  trailing?: React.ReactNode;
  /** Tags row rendered below the subtitle (chips). */
  tags?: React.ReactNode;
  /** Visual emphasis for unread / urgent rows — left orange marker. */
  emphasized?: boolean;
  /**
   * If provided, the entire row becomes a button (with focus ring + click).
   * Otherwise rendered as a plain `<li>`.
   */
  onSelect?: () => void;
  /** Optional aria-label for the row when no visible title is enough. */
  ariaLabel?: string;
}

export const SurfaceListItem = React.forwardRef<
  HTMLLIElement,
  SurfaceListItemProps
>(
  (
    {
      leading,
      title,
      subtitle,
      meta,
      trailing,
      tags,
      emphasized,
      onSelect,
      ariaLabel,
      className,
      ...rest
    },
    ref,
  ) => {
    const inner = (
      <>
        {leading ? <div className="shrink-0">{leading}</div> : null}
        <div className="min-w-0 flex-1">
          {meta ? (
            <div className="mb-0.5 flex items-center gap-1.5 text-[11px] text-ink-3">
              {meta}
            </div>
          ) : null}
          <div className="truncate font-medium text-[13.5px] text-ink-1">
            {title}
          </div>
          {subtitle ? (
            <div className="mt-0.5 truncate text-[12px] text-ink-2">
              {subtitle}
            </div>
          ) : null}
          {tags ? (
            <div className="mt-1 flex flex-wrap items-center gap-1.5">{tags}</div>
          ) : null}
        </div>
        {trailing ? (
          <div className="ml-2 shrink-0 text-[11px] text-ink-3">{trailing}</div>
        ) : null}
      </>
    );

    const shared = cn(
      "relative flex w-full items-start gap-3 rounded-md border border-hairline/70 bg-card/55 px-3 py-2.5 text-left transition-colors",
      emphasized && [
        "before:absolute before:top-2 before:bottom-2 before:left-0 before:w-[2px] before:rounded-r before:bg-accent",
      ],
      onSelect && [
        "hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
      ],
      className,
    );

    if (onSelect) {
      return (
        <li ref={ref} {...rest}>
          <button
            type="button"
            onClick={onSelect}
            aria-label={ariaLabel}
            className={shared}
          >
            {inner}
          </button>
        </li>
      );
    }
    return (
      <li ref={ref} className={shared} aria-label={ariaLabel} {...rest}>
        {inner}
      </li>
    );
  },
);
SurfaceListItem.displayName = "SurfaceListItem";
